/**
 * Audio playback hook for live voice chat.
 *
 * Queues and plays audio chunks from the AI in real-time.
 * Handles audio at 24kHz mono PCM format (Gemini's output format).
 */

import { useRef, useState, useCallback, useEffect } from 'react'

// Audio configuration for Gemini Live output
const OUTPUT_SAMPLE_RATE = 24000
const CHANNELS = 1

export interface AudioPlaybackOptions {
  /** Callback when playback starts */
  onPlaybackStart?: () => void
  /** Callback when playback stops/completes */
  onPlaybackStop?: () => void
  /** Callback when queue is empty */
  onQueueEmpty?: () => void
}

export interface AudioPlaybackState {
  isPlaying: boolean
  queueLength: number
}

export interface AudioPlaybackControls {
  /** Play a base64-encoded PCM audio chunk */
  play: (audioB64: string) => void
  /** Queue a raw ArrayBuffer of PCM audio */
  queue: (pcm: ArrayBuffer) => void
  /** Interrupt current playback and clear queue */
  interrupt: () => void
  /** Set volume (0-1) */
  setVolume: (volume: number) => void
}

/**
 * Convert Int16 PCM samples to Float32 for Web Audio API.
 */
function pcm16ToFloat32(pcm16: Int16Array): Float32Array {
  const float32 = new Float32Array(pcm16.length)
  for (let i = 0; i < pcm16.length; i++) {
    float32[i] = pcm16[i] / 0x8000
  }
  return float32
}

/**
 * Decode base64 string to ArrayBuffer.
 */
function base64ToArrayBuffer(base64: string): ArrayBuffer {
  const binaryString = atob(base64)
  const bytes = new Uint8Array(binaryString.length)
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i)
  }
  return bytes.buffer
}

/**
 * Hook for playing audio responses from the AI.
 *
 * @example
 * ```tsx
 * const { isPlaying, play, interrupt } = useAudioPlayback({
 *   onPlaybackStart: () => setAiSpeaking(true),
 *   onPlaybackStop: () => setAiSpeaking(false),
 * })
 *
 * // Play base64 audio chunk
 * play(audioB64)
 *
 * // Interrupt when user starts speaking
 * interrupt()
 * ```
 */
export function useAudioPlayback(options: AudioPlaybackOptions = {}): AudioPlaybackState & AudioPlaybackControls {
  const { onPlaybackStart, onPlaybackStop, onQueueEmpty } = options

  const [isPlaying, setIsPlaying] = useState(false)
  const [queueLength, setQueueLength] = useState(0)

  const audioContextRef = useRef<AudioContext | null>(null)
  const gainNodeRef = useRef<GainNode | null>(null)
  const queueRef = useRef<AudioBuffer[]>([])
  const currentSourceRef = useRef<AudioBufferSourceNode | null>(null)
  const isProcessingRef = useRef(false)

  // Initialize AudioContext lazily
  const getAudioContext = useCallback(() => {
    if (!audioContextRef.current) {
      audioContextRef.current = new AudioContext({ sampleRate: OUTPUT_SAMPLE_RATE })
      gainNodeRef.current = audioContextRef.current.createGain()
      gainNodeRef.current.connect(audioContextRef.current.destination)
    }
    // Resume if suspended (browser autoplay policy)
    if (audioContextRef.current.state === 'suspended') {
      audioContextRef.current.resume()
    }
    return audioContextRef.current
  }, [])

  // Process the audio queue
  const processQueue = useCallback(async () => {
    if (isProcessingRef.current) return
    if (queueRef.current.length === 0) {
      setIsPlaying(false)
      onPlaybackStop?.()
      onQueueEmpty?.()
      return
    }

    isProcessingRef.current = true
    setIsPlaying(true)
    onPlaybackStart?.()

    while (queueRef.current.length > 0) {
      const buffer = queueRef.current.shift()!
      setQueueLength(queueRef.current.length)

      const ctx = getAudioContext()
      const source = ctx.createBufferSource()
      source.buffer = buffer
      source.connect(gainNodeRef.current!)
      currentSourceRef.current = source

      // Play and wait for completion
      await new Promise<void>((resolve) => {
        source.onended = () => {
          currentSourceRef.current = null
          resolve()
        }
        source.start()
      })
    }

    isProcessingRef.current = false
    setIsPlaying(false)
    onPlaybackStop?.()
    onQueueEmpty?.()
  }, [getAudioContext, onPlaybackStart, onPlaybackStop, onQueueEmpty])

  // Queue raw PCM audio
  const queue = useCallback((pcm: ArrayBuffer) => {
    const ctx = getAudioContext()

    // Convert PCM to AudioBuffer
    const pcm16 = new Int16Array(pcm)
    const float32 = pcm16ToFloat32(pcm16)

    const buffer = ctx.createBuffer(CHANNELS, float32.length, OUTPUT_SAMPLE_RATE)
    buffer.getChannelData(0).set(float32)

    queueRef.current.push(buffer)
    setQueueLength(queueRef.current.length)

    // Start processing if not already
    if (!isProcessingRef.current) {
      processQueue()
    }
  }, [getAudioContext, processQueue])

  // Play base64-encoded audio
  const play = useCallback((audioB64: string) => {
    const pcm = base64ToArrayBuffer(audioB64)
    queue(pcm)
  }, [queue])

  // Interrupt current playback
  const interrupt = useCallback(() => {
    // Stop current source
    if (currentSourceRef.current) {
      currentSourceRef.current.stop()
      currentSourceRef.current = null
    }

    // Clear queue
    queueRef.current = []
    setQueueLength(0)
    isProcessingRef.current = false
    setIsPlaying(false)
  }, [])

  // Set volume
  const setVolume = useCallback((volume: number) => {
    if (gainNodeRef.current) {
      gainNodeRef.current.gain.value = Math.max(0, Math.min(1, volume))
    }
  }, [])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      interrupt()
      if (audioContextRef.current) {
        audioContextRef.current.close().catch(() => {
          // Ignore close errors
        })
      }
    }
  }, [interrupt])

  return {
    isPlaying,
    queueLength,
    play,
    queue,
    interrupt,
    setVolume,
  }
}

export default useAudioPlayback
